import { useEffect, useRef, useState } from 'react';
import {
  Flex,
  Text,
  Button,
  Icon,
  Separator,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import { FilterConditionRow } from './FilterConditionRow';
import {
  type IFilterCondition,
  type IFilterableField,
  getOperatorsForDescriptor,
  getDefaultValueForDescriptor,
} from './filter-utils';

/**
 * Props for the TriggerFilters component
 */
export interface ITriggerFiltersProps {
  /** Available filterable fields (top-level event fields) */
  fields: IFilterableField[];
  /** Current filter conditions */
  conditions: IFilterCondition[];
  /** Callback when conditions change */
  onConditionsChange: (conditions: IFilterCondition[]) => void;
  /** Per-condition validation errors keyed by condition id */
  errors?: Record<string, string>;
  /** Whether the filters are read-only */
  readOnly?: boolean;
  /**
   * Condition id the assistant just added. Its row pulses + shows a ✨ marker so
   * the auto-added filter is visible.
   */
  highlightedConditionId?: string | null;
  /**
   * Monotonic nonce bumped on every filter add; the highlighted row's effect
   * depends on it so re-marking the same condition replays the pulse.
   */
  highlightNonce?: number;
}

/**
 * TriggerFilters Component
 *
 * A collapsible "Filters" card that lets the user define filter conditions on a
 * trigger's top-level event fields. Each condition is a
 * [Field] [Operator] [Value] row; conditions are combined with AND. Mirrors the
 * `WebhookConfiguration` card visually.
 *
 * @param props - Component props
 * @returns JSX element containing the filters card
 */
export function TriggerFilters({
  fields,
  conditions,
  onConditionsChange,
  errors,
  readOnly = false,
  highlightedConditionId,
  highlightNonce,
}: ITriggerFiltersProps) {
  const { t } = useTranslation('automation-inputs');

  const [isOpen, setIsOpen] = useState(() => conditions.length > 0);

  // Expand the section whenever a condition is added — especially when the
  // assistant adds one while the section is collapsed, so the user sees that a
  // filter was filled in for them rather than it landing silently.
  const prevConditionCount = useRef(conditions.length);
  // eslint-disable-next-line no-restricted-syntax -- reason: reveal the section when conditions grow (e.g. assistant-added filters)
  useEffect(() => {
    if (conditions.length > prevConditionCount.current) {
      setIsOpen(true);
    }
    prevConditionCount.current = conditions.length;
  }, [conditions.length]);

  const handleAddFilter = () => {
    const firstField = fields[0];
    if (!firstField) return;
    const operators = getOperatorsForDescriptor(firstField.descriptor);
    onConditionsChange([
      ...conditions,
      {
        id: crypto.randomUUID(),
        field: firstField.name,
        operator: operators[0] ?? 'equals',
        value: getDefaultValueForDescriptor(firstField.descriptor),
      },
    ]);
  };

  const handleConditionChange = (next: IFilterCondition) => {
    onConditionsChange(
      conditions.map(condition =>
        condition.id === next.id ? next : condition,
      ),
    );
  };

  const handleRemove = (id: string) => {
    onConditionsChange(conditions.filter(condition => condition.id !== id));
  };

  const handleClearAll = () => {
    onConditionsChange([]);
  };

  return (
    <>
      {/* Separator between configuration and filters */}
      <Separator className="my-2" />

      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className="w-full bg-secondary rounded-lg p-5"
      >
        {/* Collapsible Header */}
        <CollapsibleTrigger className="w-full">
          <Flex
            direction="row"
            align="start"
            justify="between"
            className="px-2.5 pt-2 pb-2 cursor-pointer hover:bg-accent/50 rounded-md transition-colors"
          >
            <Flex direction="column" gap={1} align="start">
              <Text className="font-medium text-md">
                {t('webhookDialog.filters.title')}
              </Text>
              <Text className="text-sm text-muted-foreground text-left">
                {t('webhookDialog.filters.description')}
              </Text>
            </Flex>
            <Icon
              type={isOpen ? 'ChevronUp' : 'ChevronDown'}
              size="sm"
              className="text-muted-foreground shrink-0 mt-0.5"
            />
          </Flex>
        </CollapsibleTrigger>

        {/* Collapsible Content - Condition rows */}
        <CollapsibleContent className="pt-2">
          <Flex direction="column" gap={3} className="px-2.5">
            {conditions.length === 0 ? (
              <Text className="text-sm text-muted-foreground">
                {t('webhookDialog.filters.empty')}
              </Text>
            ) : (
              conditions.map(condition => (
                <FilterConditionRow
                  key={condition.id}
                  fields={fields}
                  condition={condition}
                  onChange={handleConditionChange}
                  onRemove={() => handleRemove(condition.id)}
                  error={errors?.[condition.id]}
                  readOnly={readOnly}
                  highlighted={highlightedConditionId === condition.id}
                  highlightNonce={highlightNonce}
                />
              ))
            )}

            {/* Action Buttons - hidden in read-only mode */}
            {!readOnly && (
              <Flex direction="row" justify="between" width="full">
                <Button variant="outline" onClick={handleAddFilter}>
                  <Icon type="Plus" className="size-4" />
                  {t('webhookDialog.filters.addFilter')}
                </Button>
                {conditions.length > 0 && (
                  <Button variant="outline" onClick={handleClearAll}>
                    {t('webhookDialog.filters.clearAll')}
                  </Button>
                )}
              </Flex>
            )}
          </Flex>
        </CollapsibleContent>
      </Collapsible>
    </>
  );
}
